/**
 * @fileoverview ClubOS Squad Management page — player cards with availability status
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftAvatar from "components/SoftAvatar";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Mock data
import { squad } from "data/mockData";

// TODO: Replace with API call when backend is ready
// import { squadService } from "api";

const TABS = ["First Team", "U21", "U18", "Loans Out"];

const STATUS_COLOR = {
  Available: "success",
  Injured: "error",
  Suspended: "warning",
  International: "info",
};

const STATUS_DOT = {
  Available: "#4caf50",
  Injured: "#f44336",
  Suspended: "#ff9800",
  International: "#2196f3",
};

/** Depth chart positions */
const DEPTH = [
  { pos: "GK", min: 2 }, { pos: "CB", min: 3 }, { pos: "LB", min: 2 }, { pos: "RB", min: 2 },
  { pos: "CDM", min: 2 }, { pos: "CM", min: 3 }, { pos: "CAM", min: 2 },
  { pos: "LW", min: 2 }, { pos: "RW", min: 2 }, { pos: "ST", min: 2 },
];

function SquadManagement() {
  const [activeTab, setActiveTab] = useState(0);

  // TODO: Uncomment when API is ready
  // const [players, setPlayers] = useState(squad);
  // useEffect(() => { squadService.getSquad().then(setPlayers).catch(console.error); }, []);

  const selectedSquad = TABS[activeTab];
  const filtered = squad.filter((p) => p.squad === selectedSquad || (selectedSquad === "First Team" && !["U21","U18","Loans Out"].includes(p.squad)));

  const available = squad.filter((p) => p.status === "Available").length;
  const injured = squad.filter((p) => p.status === "Injured").length;
  const intl = squad.filter((p) => p.status === "International").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Squad Management
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            FC Metropolitan · {squad.length} registered players
          </SoftTypography>
        </SoftBox>

        {/* Status KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Available" }} count={String(available)} percentage={{ color: "success", text: "fit to play" }} icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Injured" }} count={String(injured)} percentage={{ color: "error", text: "unavailable" }} icon={{ color: "error", component: "healing" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "International" }} count={String(intl)} percentage={{ color: "info", text: "on duty" }} icon={{ color: "info", component: "public" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Squad" }} count={String(squad.length)} percentage={{ color: "dark", text: "registered" }} icon={{ color: "dark", component: "people" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Tabs + Player Grid */}
        <Card>
          <SoftBox p={2}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: "1px solid #f0f0f0" }}>
              {TABS.map((t) => <Tab key={t} label={t} sx={{ textTransform: "none" }} />)}
            </Tabs>

            <Grid container spacing={2}>
              {filtered.map((player) => (
                <Grid key={player.id} item xs={12} sm={6} md={4} lg={3}>
                  <SoftBox
                    border="1px solid #e9ecef"
                    borderRadius="lg"
                    p={2}
                    sx={{ "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)", cursor: "pointer" } }}
                  >
                    <SoftBox display="flex" alignItems="center" mb={1}>
                      <SoftBox position="relative" mr={1.5}>
                        <SoftAvatar
                          sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, width: 40, height: 40 }}
                        >
                          {player.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                        </SoftAvatar>
                        <SoftBox
                          position="absolute"
                          bottom={-2}
                          right={-2}
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{ backgroundColor: STATUS_DOT[player.status], border: "2px solid white" }}
                        />
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="button" fontWeight="bold" display="block" lineHeight={1.2}>
                          {player.name}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text">
                          {player.flag} {player.nationality}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                      <SoftBadge badgeContent={player.position} color="info" size="xs" container />
                      <SoftTypography variant="caption" color="text">Age {player.age}</SoftTypography>
                    </SoftBox>
                    <SoftBox mt={1}>
                      <SoftBadge
                        badgeContent={player.status}
                        color={STATUS_COLOR[player.status]}
                        size="xs"
                        container
                      />
                    </SoftBox>
                  </SoftBox>
                </Grid>
              ))}
            </Grid>
          </SoftBox>
        </Card>

        {/* Depth Chart */}
        <SoftBox mt={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                Squad Depth by Position
              </SoftTypography>
              <Grid container spacing={1}>
                {DEPTH.map(({ pos, min }) => {
                  const count = squad.filter((p) => p.position === pos).length;
                  const isLow = count < min;
                  return (
                    <Grid key={pos} item xs={6} sm={4} md={2}>
                      <SoftBox
                        textAlign="center"
                        p={1.5}
                        borderRadius="md"
                        bgcolor={isLow ? "#fff3e0" : "#f1f8e9"}
                        border={`1px solid ${isLow ? "#ffb74d" : "#a5d6a7"}`}
                      >
                        <SoftTypography variant="caption" fontWeight="bold" display="block">
                          {pos}
                        </SoftTypography>
                        <SoftTypography variant="h5" fontWeight="bold" color={isLow ? "warning" : "success"}>
                          {count}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text">
                          min. {min}
                        </SoftTypography>
                      </SoftBox>
                    </Grid>
                  );
                })}
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default SquadManagement;
